import * as React from 'react';
import { useQuery } from '@apollo/client';
import { PanelRow, PanelCell, Grid, Cell, Icon } from '@teamsnap/teamsnap-ui';
import { validEmail } from '../../core/validation';
import { QUERY as QueryStaff } from '../../core/gql/queries/show-all-staff';
import FormElementHook from './form-element-hook';
import { displayFirstNameLast } from '../../core/utils';

const CreateElement = FormElementHook as new () => FormElementHook;
export interface Props {
  useHookResponse: any;
  selectedOrganizationId: number | null;
  currentOwner: any;
}

const OrgAdminFields: React.FunctionComponent<Props> = ({ useHookResponse, selectedOrganizationId, currentOwner }) => {
  const queryStaff = useQuery(QueryStaff, {
    variables: { organizationId: selectedOrganizationId },
    skip: !selectedOrganizationId,
    errorPolicy: 'all',
  });
  const { register, watch, getValues, setValue } = useHookResponse;
  const [disableCheckbox, setDisableCheckbox] = React.useState<boolean>(false);

  // Tried to consolidate this piece in on variable but caused infinite renders.
  const [originalFirstName, setOriginalFirstName] = React.useState<string>(null);
  const [originalLastName, setOriginalLastName] = React.useState<string>(null);
  const [originalEmail, setOriginalEmail] = React.useState<string>(null);

  let watchOwnerFirstName = '';
  let watchOwnerLastName = '';
  let watchOwnerEmail = '';

  const ownerExists = currentOwner ? true : false;

  if (!ownerExists) {
    watchOwnerFirstName = watch('ownerFirstName');
    watchOwnerLastName = watch('ownerLastName');
    watchOwnerEmail = watch('ownerEmail');
  }

  React.useEffect(() => {
    if (!originalFirstName) {
      setOriginalFirstName(getValues('ownerFirstName'));
    }
    if (!originalLastName) {
      setOriginalLastName(getValues('ownerLastName'));
    }
    if (!originalEmail) {
      setOriginalEmail(getValues('ownerEmail'));
    }
  }, [getValues, originalFirstName, originalLastName, originalEmail]);

  React.useEffect(() => {
    if (
      originalFirstName !== watchOwnerFirstName ||
      originalLastName !== watchOwnerLastName ||
      originalEmail !== watchOwnerEmail
    ) {
      setDisableCheckbox(true);
      setValue('resendInvite', true);
    } else {
      setDisableCheckbox(false);
      setValue('resendInvite', false);
    }
  }, [
    setValue,
    originalFirstName,
    originalLastName,
    originalEmail,
    watchOwnerFirstName,
    watchOwnerLastName,
    watchOwnerEmail,
  ]);

  const setupOwnerStaff = React.useCallback((currentOwner) => {
    if (currentOwner) {
      setValue('ownerFirstName', currentOwner.firstName);
      setValue('ownerLastName', currentOwner.lastName);
      setValue('ownerEmail', currentOwner.emailAddress);
    }
  }, [setValue]);

  React.useEffect(() => {
    setupOwnerStaff(currentOwner);
  }, [setupOwnerStaff, currentOwner]);

  const getStaffOptions = () => {
    const staffData = queryStaff.data?.showOrganization?.staff || [];
    const staff = staffData.filter((staff) => staff.id !== currentOwner.id && staff.invitationStatus == 'accepted');

    let options = staff.map((staff) => ({
      text: `${displayFirstNameLast(staff)} / ${staff.emailAddress}`,
      value: `${staff.userId}-${staff.emailAddress}`,
    }));
    options = [{ text: 'Select a new owner', value: '' }, ...options];

    return options;
  };

  return (
    <PanelRow isWithCells={true} mods="Panel-row--header">
      <PanelCell mods="Panel-cell--header u-size3of12 u-flex">
        <h4>Owner</h4>
      </PanelCell>
      <PanelCell mods="u-size9of12">
        <>
          {currentOwner ? (
            <Grid isWithGutter={true}>
              <Cell mods="u-size5of12">
                <div className="FieldGroup">
                  <label htmlFor="current-org-owner" className="FieldGroup-label u-padTopMd">
                    Current Org Owner
                  </label>
                  <div className="u-textNormal u-padTopSm ">{`${currentOwner.firstName} ${currentOwner.lastName}`}</div>
                  <div className="u-textNormal u-padBottomSm">{`${currentOwner.emailAddress}`}</div>
                </div>
                <div className="accepted">
                  <Icon name="check" mods="u-fontSizeMd u-spaceRightSm" /> Owner invitation accepted.
                </div>
              </Cell>
              <Cell mods="u-size6of12">
                <CreateElement
                  useHookResponse={useHookResponse}
                  inputRef={register({
                    required: false,
                  })}
                  type="select"
                  field="newOwnerUserId"
                  label={
                    <div>
                      New Org Owner
                      <div className="u-textNormal u-fontSizeSm">
                        Select an existing org staff member to be the new org owner
                      </div>
                    </div>
                  }
                  options={getStaffOptions()}
                />
              </Cell>
            </Grid>
          ) : (
            <>
              <Grid isWithGutter={true}>
                <Cell mods="u-size12of12 u-colorWarning">
                  <div>
                    Any changes to name or email will send a new invite and void all existing invites. Please inform the
                    customer to avoid confusion.
                  </div>
                </Cell>
              </Grid>
              <Grid isWithGutter={true}>
                <Cell mods="u-size5of12">
                  <CreateElement
                    useHookResponse={useHookResponse}
                    inputRef={register({
                      required: 'Can\'t be blank.',
                    })}
                    type="text"
                    field={'ownerFirstName'}
                    placeholder="First Name"
                    label="First Name"
                    disabled={ownerExists}
                  />
                </Cell>
                <Cell mods="u-size4of12">
                  <CreateElement
                    useHookResponse={useHookResponse}
                    inputRef={register({
                      required: 'Can\'t be blank.',
                    })}
                    type="text"
                    field="ownerLastName"
                    placeholder="Last Name"
                    label="Last Name"
                    disabled={ownerExists}
                  />
                </Cell>
              </Grid>
              <br />
              <Grid isWithGutter={true}>
                <Cell mods="u-size5of12">
                  <CreateElement
                    useHookResponse={useHookResponse}
                    inputRef={register({
                      required: 'Please enter a valid email address.',
                      validate: (value) => validEmail(value) || 'Please enter a valid email address.',
                    })}
                    type="text"
                    field="ownerEmail"
                    placeholder="Email"
                    label="Email"
                    disabled={ownerExists}
                  />
                </Cell>
                {!isNaN(selectedOrganizationId) && (
                  <Cell mods="u-size4of12 u-padTopXxl">
                    <CreateElement
                      useHookResponse={useHookResponse}
                      inputRef={register({
                        required: false,
                      })}
                      type="checkbox"
                      field="resendInvite"
                      label="Resend owner invitation"
                      disabled={disableCheckbox}
                    />
                  </Cell>
                )}
              </Grid>
            </>
          )}
        </>
      </PanelCell>
    </PanelRow>
  );
};

export default OrgAdminFields;
